import React from 'react';
import Device, { DeviceStatus } from '../../models/device';
import { Box, Checkbox, TableCell, Tooltip } from '@mui/material';
import Command from '../../models/command';
import EditableField from './editableField';
import HeartbeatCell from './heartbeatCell';
import { formatDateTime, isValidMacAddress } from '../../utils';
import CommandState from './commandState';

// Tiny inline dot rendered before the device label when the server's status
// rollup has flagged anything. Tooltip text comes from the rolled-up `issues`
// strings so any future signal added on the server appears here for free.
function StatusDot({ status, issues }: { status?: DeviceStatus, issues?: string[] }) {
  if (!status || status === 'ok') return null;
  const color = status === 'critical' ? 'error.main' : 'warning.main';
  const title = (issues && issues.length) ? issues.join(' · ') : status;
  return (
    <Tooltip title={title} arrow>
      <Box component="span" sx={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: color,
        mr: 1,
        verticalAlign: 'middle',
        flexShrink: 0,
      }} />
    </Tooltip>
  );
}

function formatCell(value?: string, field?: keyof Device): React.ReactNode {
  // Convert MAC address to uppercase if it exists
  if (value && field === 'mac' && isValidMacAddress(value)) {
    value = value.toUpperCase();
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (value) {
      navigator.clipboard.writeText(value);
    }
  };

  return (
    <TableCell
      key={field}
      align="left"
      onDoubleClick={handleDoubleClick}
    >
      {value}
    </TableCell>
  );
}

export interface CellHandlers {
  isSelected: boolean;
  checkboxID: string;
  heartbeatStale?: boolean;
  subscriptionStatus?: string;
  canEditLabel?: boolean;
  showCheckbox?: boolean;
  onCheck?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  refresh: () => void;
  updateLabel: (label: string) => Promise<void>;
  // Server-side health rollup. status === 'ok' (or absent) renders nothing
  // extra; warning/critical surface as a dot prefixed to the Label cell.
  status?: DeviceStatus;
  issues?: string[];
}

interface DeviceTableCellProps {
  value: string
  field?: keyof Device | undefined
  handlers?: CellHandlers
  ongoingCommand?: Command
}

// Helper function to render a cell based on field type
function DeviceTableCell({ value, field, handlers, ongoingCommand }: DeviceTableCellProps): React.ReactNode {
  if (!field) {
    if (!handlers?.showCheckbox) return null;
    return (
      <TableCell key='checkbow' padding="checkbox">
        <Checkbox
          checked={handlers?.isSelected}
          disabled={handlers?.heartbeatStale}
          inputProps={{ 'aria-labelledby': handlers?.checkboxID }}
          onClick={(e) => {
            e.stopPropagation();
            if (handlers?.onCheck)
              handlers.onCheck(e);
          }}
        />
      </TableCell>
    );
  }

  switch (field) {
    case 'label':
      if (handlers?.canEditLabel) {
        return (
          <TableCell
            key={field}
            component="th"
            id={handlers?.checkboxID}
            scope="props.device"
            onClick={(e) => e.stopPropagation()}
            sx={{ whiteSpace: 'nowrap' }}
          >
            <StatusDot status={handlers?.status} issues={handlers?.issues} />
            <EditableField
              value={value}
              placeholder="add label"
              onUpdate={(label: string) => {
                handlers?.updateLabel(label).then(() => {
                  handlers.refresh();
                });
              }}
              textfieldProps={{
                sx: {
                  width: 'auto',
                  minWidth: '200px',
                  '& .MuiInput-root::before': { borderBottom: 'none' },
                  '& .MuiInput-root::after': { borderBottom: 'none' },
                  '& .MuiInput-root:hover::before': { borderBottom: 'none !important' }
                }
              }}
            />
          </TableCell>
        );
      }
      return (
        <TableCell
          key={field}
          component="th"
          id={handlers?.checkboxID}
          scope="props.device"
          sx={{ whiteSpace: 'nowrap', minWidth: '200px' }}
        >
          <StatusDot status={handlers?.status} issues={handlers?.issues} />
          {value}
        </TableCell>
      );
    case 'last_heartbeat':
      return (<HeartbeatCell key={field} value={value} isStale={handlers?.heartbeatStale} subscriptionStatus={handlers?.subscriptionStatus} />);
    case 'created':
      return (
        <TableCell
          key={field}
          align="left"
          onDoubleClick={(e) => {
            e.stopPropagation();
            if (value) {
              navigator.clipboard.writeText(value);
            }
          }}
        >
          {value && formatDateTime(value)}
        </TableCell>
      );
    case 'command':
      return (
        <TableCell
          key={field} align="left" onClick={(e) => e.stopPropagation()}>
          <CommandState command={ongoingCommand || {}} />
        </TableCell>
      );
    default:
      return formatCell(value, field);
  }
};

export default React.memo(DeviceTableCell);